import { useLocation, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { useFlavorTheme } from '@/theme'
import { useAuth } from '@/hooks/useAuth'
import { menuConfig } from './menuConfig'
import LibrarySelector from '@/components/stores/LibrarySelector'

export const DRAWER_WIDTH = 260

const Sidebar = ({ open, onClose, variant = 'permanent' }) => {
    const location = useLocation()
    const { t } = useTranslation()
    const { sidebarColors, resolvedMode } = useFlavorTheme()
    const { hasPermission, isAdmin, isSupervisor } = useAuth()

    const currentPath = location.pathname.split('/')[1] || 'dashboard'

    // Logo de marca según el dominio; los svg de logos/ son monocromos blancos, se invierten en modo claro
    const isDialocFlavor = window.location.hostname === 'cloud.dialocidlibrary.com'
    const brandLogo = isDialocFlavor ? 'dialocid.svg' : 'techkovery.svg'
    const brandLabel = isDialocFlavor ? 'Dialoc ID' : 'Techkovery'
    const brandHeight = isDialocFlavor ? 23 : 32

    // Solo cierra el drawer; la navegación la maneja RouterLink (soporta click central/ctrl+click)
    const handleNavigate = () => {
        if (variant === 'temporary') onClose?.()
    }

    const drawerContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Brand header — misma altura que el Toolbar (64px) */}
            <Box
                sx={{
                    px: 2.5,
                    minHeight: 64,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                <Box
                    component='img'
                    src={`${import.meta.env.BASE_URL}logos/${brandLogo}`}
                    alt={brandLabel}
                    sx={{
                        height: brandHeight,
                        filter: resolvedMode === 'light' ? 'invert(1)' : 'none'
                    }}
                />
            </Box>

            <Divider sx={{ borderColor: sidebarColors.dividerColor, mx: 2 }} />

            {/* Library selector */}
            <Box sx={{ pt: 1.5 }}>
                <LibrarySelector colors={sidebarColors} />
            </Box>

            <Divider sx={{ borderColor: sidebarColors.dividerColor, mx: 2 }} />

            {/* Menu groups */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    py: 1,
                    scrollbarColor: `${sidebarColors.scrollThumb} transparent`,
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                        width: 8
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: sidebarColors.scrollThumb,
                        borderRadius: 4
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: sidebarColors.scrollThumbHover
                    }
                }}>
                {menuConfig.map((group) => {
                    const visibleItems = group.items.filter((item) => {
                        if (item.key === 'users') return isAdmin || isSupervisor
                        if (item.permission === 'admin') return isAdmin
                        return hasPermission(item.permission)
                    })
                    if (visibleItems.length === 0) return null

                    return (
                        <Box key={group.group} sx={{ mb: 0.5 }}>
                            <Typography
                                variant='overline'
                                sx={{
                                    px: 3,
                                    py: 1,
                                    display: 'block',
                                    color: sidebarColors.text,
                                    opacity: 0.5,
                                    fontSize: '0.65rem',
                                    letterSpacing: '0.1em'
                                }}>
                                {t(group.group)}
                            </Typography>
                            <List disablePadding>
                                {visibleItems.map((item) => {
                                    const isActive = currentPath === item.key
                                    const Icon = item.icon
                                    return (
                                        <ListItem key={item.key} disablePadding sx={{ px: 1.5 }}>
                                            <ListItemButton
                                                component={RouterLink}
                                                to={`/${item.key}`}
                                                onClick={handleNavigate}
                                                sx={{
                                                    borderRadius: 1.5,
                                                    mb: 0.25,
                                                    color: isActive
                                                        ? sidebarColors.activeText
                                                        : sidebarColors.text,
                                                    bgcolor: isActive
                                                        ? sidebarColors.activeBg
                                                        : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: isActive
                                                            ? sidebarColors.activeBg
                                                            : sidebarColors.hoverBg
                                                    }
                                                }}>
                                                <ListItemIcon
                                                    sx={{ color: 'inherit', minWidth: 36 }}>
                                                    <Icon fontSize='small' />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={t(item.label)}
                                                    primaryTypographyProps={{
                                                        fontSize: '0.875rem'
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )

    return (
        <Drawer
            variant={variant}
            open={open}
            onClose={onClose}
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    bgcolor: sidebarColors.bg,
                    border: 'none',
                    // Evita que el notch tape la cabecera de marca en el drawer temporal
                    pt: 'env(safe-area-inset-top)'
                }
            }}>
            {drawerContent}
        </Drawer>
    )
}

export default Sidebar
